import { useCallback, useEffect, useState } from "react";
import { MessageSquare, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { MessageThread } from "@/components/site/MessageThread";
import { api, formatApiError } from "@/lib/api";

export function useDayComments(day, studentId) {
  const [comments, setComments] = useState([]);
  const base = studentId ? `/admin/students/${studentId}/comments` : "/course/comments";
  const load = useCallback(() => {
    api.get(base, { params: { day } }).then((r) => setComments(r.data)).catch(() => {});
  }, [base, day]);
  useEffect(load, [load]);
  const markRead = (section, me) => {
    const flag = me === "host" ? "read_by_host" : "read_by_student";
    api.post(`${base}/read`, { day, section }).catch(() => {});
    setComments((c) => c.map((x) => (x.section === section ? { ...x, [flag]: true } : x)));
  };
  const post = async (section, body) => {
    try {
      const { data } = await api.post(base, { day, section, body });
      setComments((c) => [...c, data]);
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };
  return { comments, post, markRead };
}

export function SectionThread({ section, comments, post, markRead, me, label }) {
  const items = comments.filter((c) => c.section === section);
  const unread = items.filter((c) => (me === "host" ? !c.read_by_host : !c.read_by_student)).length;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (items.length && me === "student") setOpen(true);
  }, [items.length, me]);
  useEffect(() => {
    if (open && unread > 0 && markRead) markRead(section, me);
  }, [open, unread]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="mt-6 rounded-xl border border-[#1A9AA8]/30 bg-[#1A9AA8]/5" data-testid={`section-thread-${section}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-[#147C88]" data-testid={`section-thread-toggle-${section}`}>
        <span className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> {label || (me === "host" ? "Advice for the student" : "Talk with Mr. Greg about this section")} ({items.length})
          {unread > 0 && <span className="rounded-full bg-[#F08A3C] text-white text-[10px] px-2 py-0.5">{unread} new</span>}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          <MessageThread messages={items} onSend={(b) => post(section, b)} me={me} placeholder={me === "host" ? "Write advice about this section..." : "Ask a question or reply to Mr. Greg..."} />
        </div>
      )}
    </div>
  );
}
